import { getStrokeOutlinePoints, getStrokePoints } from 'perfect-freehand';

import { getConnectedGroups } from '../../../../common/dataStructures/graphUtils';
import { isHexColorFormat } from '../../../../common/colors/colorSpaceUtil';
import { translateFreehandPoint } from './perfectFreehandUtils';
import * as rectLib from '../../../../common/maths/geometry/rect';
import type { Rect } from '../../../../common/maths/geometry/rect/rectTypes';
import { DRAWING_PATH_DEFAULTS } from './drawingDefaults';

import { DRAWING_COLORS } from '../../../../common/colors/colorConstants';
import { type StrokePoint, getSvgPathBoundingRect, transformSvgPathData } from './utils/drawingSvgPathUtils';

// Debug
import './drawingGlobalState';
import type { Point } from '../../../../common/maths/geometry/pointTypes';

export type FreehandPoint = [number, number] | [number, number, number];

export type DrawingPath = {
    id?: string;
    points: FreehandPoint[];
    stroke?: StrokePoint[];
    size: number;
    hasPressure?: boolean;
    thinning?: number;
    smoothing?: number;
    streamline?: number;
    startTaper?: number;
    endTaper?: number;
    last?: boolean;
    color?: string;
    svgPath?: string;
    isStrokeOnly?: boolean;
    [key: string]: unknown;
};

type PathIdsToShiftMap = Record<string, boolean>;

type DrawingGlobalState = {
    drawing: {
        thinning: number;
        smoothing: number;
        streamline: number;
        startTaper: number;
        endTaper: number;
    };
};

export const round = (number: number) => Math.round(number);

export const getStrokeColor = (color?: string) => {
    if (isHexColorFormat(color)) return color;

    const drawingColor = Object.values(DRAWING_COLORS).find((value) => value.name === color);
    return drawingColor?.hex ?? undefined;
};

/**
 * Annotations must always save the stroke colour, because they can get saved downloaded as an SVG.
 */
export const getStrokeColorWithFallback = (color?: string) => getStrokeColor(color) || '#323b4a';

const getFreehandPressure = (point: FreehandPoint, hasPressure: boolean): number => {
    if (!hasPressure) return 0.5;

    const pressure = point[2];
    if (typeof pressure !== 'number' || Number.isNaN(pressure)) return 0.5;

    return Math.max(0, Math.min(1, pressure));
};

// turns input points & size into a perfect-freehand list of stroke points
export const getPathStroke = (args: DrawingPath, forceOutline = false): StrokePoint[] => {
    const drawing = (global as typeof globalThis & Partial<DrawingGlobalState>).drawing;

    const {
        points,
        size,
        hasPressure = false,
        thinning = drawing?.thinning ?? DRAWING_PATH_DEFAULTS.thinning,
        smoothing = drawing?.smoothing ?? DRAWING_PATH_DEFAULTS.smoothing,
        streamline = drawing?.streamline ?? DRAWING_PATH_DEFAULTS.streamline,
        startTaper = drawing?.startTaper ?? DRAWING_PATH_DEFAULTS.startTaper,
        endTaper = drawing?.endTaper ?? DRAWING_PATH_DEFAULTS.endTaper,
        last = true,
    } = args;

    // If there's no thinning - don't get points on the edges of the lines, just use the
    // the actual drawn points, then rely on the stroke-width to make a nicely rendered thick line
    if (!thinning && !forceOutline) return points.map((point) => [point[0], point[1]]);

    const pointsPath = points.map((point) => [point[0], point[1], getFreehandPressure(point, hasPressure)]);

    const perfectStrokePoints = getStrokePoints(pointsPath, {
        size,
        // The effect of pressure - accentuate when the input device supports pressure
        thinning,
        // How much to soften the stroke edges
        smoothing,
        // How much to streamline the stroke
        streamline,
        start: {
            taper: startTaper,
        },
        end: {
            taper: endTaper,
        },
        simulatePressure: !hasPressure,
        last,
    });

    return getStrokeOutlinePoints(perfectStrokePoints, {
        size,
        thinning,
        smoothing,
        streamline,
        start: { taper: startTaper },
        end: { taper: endTaper },
        simulatePressure: !hasPressure,
        last,
    });
};

export const getStrokeBoundingRect = (stroke: StrokePoint[], margin = 0): Rect => {
    let left = Infinity;
    let top = Infinity;
    let right = 0;
    let bottom = 0;

    stroke.forEach(([pointX, pointY]) => {
        left = Math.min(pointX - margin, left);
        top = Math.min(pointY - margin, top);

        right = Math.max(pointX + margin, right);
        bottom = Math.max(pointY + margin, bottom);
    });

    return {
        x: left,
        y: top,
        top,
        right,
        bottom,
        left,
        width: right - left,
        height: bottom - top,
    };
};

const getPathBoundingRectMargin = (path: DrawingPath) => {
    const { isStrokeOnly, thinning = 0, svgPath, size } = path;
    return (svgPath && isStrokeOnly) || thinning === 0 ? size / 2 : 0;
};

export const getPathBoundingRect = (path: DrawingPath): Rect => {
    const margin = getPathBoundingRectMargin(path);

    if (path.svgPath) {
        const svgRect = getSvgPathBoundingRect(path.svgPath);

        if (svgRect) {
            return {
                ...svgRect,
                x: svgRect.x - margin,
                y: svgRect.y - margin,
                top: svgRect.top - margin,
                right: svgRect.right + margin,
                bottom: svgRect.bottom + margin,
                left: svgRect.left - margin,
                width: svgRect.width + margin * 2,
                height: svgRect.height + margin * 2,
            };
        }
    }

    const stroke = getPathStroke(path);
    return getStrokeBoundingRect(stroke, margin);
};

export const getPathGroups = ({ paths, gridSize }: { paths: DrawingPath[]; gridSize: number }): string[][] => {
    if (!paths || !paths.length) return [];

    const padding = 2 * gridSize;

    const boundingRectMap = paths.reduce<Record<string, Rect>>((acc, path) => {
        if (!path.id) return acc;

        let rect = getPathBoundingRect(path);
        rect = rectLib.addPadding(padding, padding, rect);

        acc[path.id] = rect;

        return acc;
    }, {});

    const overlapGraph = rectLib.getRectOverlapGraph(boundingRectMap) as Record<string, string[]>;
    return getConnectedGroups(overlapGraph) as string[][];
};

const shouldShiftPath = (path: DrawingPath, pathIdsToShiftMap?: PathIdsToShiftMap) => {
    if (!pathIdsToShiftMap) return true;
    if (!path.id) return false;
    return !!pathIdsToShiftMap[path.id];
};

const getTranslatedPath = <T extends DrawingPath>(offset: Point, path: T): T => {
    const points = path.points.map((point) => translateFreehandPoint(offset)(point) as FreehandPoint);

    if (path.svgPath) {
        return {
            ...path,
            points,
            svgPath: transformSvgPathData({
                pathData: path.svgPath,
                translation: offset,
            }),
        };
    }

    const stroke = getPathStroke({ ...path, points });

    return {
        ...path,
        points,
        stroke,
    };
};

export const shiftPaths = <T extends DrawingPath>(
    offset: Point,
    paths: T[],
    pathIdsToShiftMap?: PathIdsToShiftMap,
): T[] =>
    paths.map((path) => {
        if (!shouldShiftPath(path, pathIdsToShiftMap)) return path;
        return getTranslatedPath(offset, path);
    });

export const shiftPathsToTop = <T extends DrawingPath>(
    offset: Point,
    paths: T[],
    pathIdsToShiftMap?: PathIdsToShiftMap,
): T[] => [
    ...paths.filter((path) => !shouldShiftPath(path, pathIdsToShiftMap)),
    ...paths.filter((path) => shouldShiftPath(path, pathIdsToShiftMap)).map((path) => getTranslatedPath(offset, path)),
];

export const reorderPathsToTop = <T extends DrawingPath>(
    paths: T[],
    pathIdsToShiftMap: PathIdsToShiftMap = {},
): T[] => [
    ...paths.filter((path) => !path.id || !pathIdsToShiftMap[path.id]),
    ...paths.filter((path) => !!path.id && !!pathIdsToShiftMap[path.id]),
];

export const reorderPathsToBottom = <T extends DrawingPath>(
    paths: T[],
    pathIdsToShiftMap: PathIdsToShiftMap = {},
): T[] => [
    ...paths.filter((path) => !!path.id && !!pathIdsToShiftMap[path.id]),
    ...paths.filter((path) => !path.id || !pathIdsToShiftMap[path.id]),
];
